import { getAuth } from "firebase/auth";
import { USERS_API_BASE_URL } from "../config/api";

const BASE_URL = USERS_API_BASE_URL;

// --- INTERFACES ---

export type MachineAccountService = "storage" | "bigquery";

export interface MachineAccount {
  uid: string;
  keyLabel: string;
  apiKey: string;
  serviceAccountEmail: string;
  service?: MachineAccountService;
  personalDatasetId?: string;
  realtimeViewId?: string;
}

export interface CreateMachineAccountRequest {
  keyLabel: string;
  service: MachineAccountService;
  createDataset?: string;
  createRealtimeDataView?: boolean;
}

export interface ApiKeyInfo {
  keyLabel: string;
  updatedAt: string;
  serviceAccountEmail?: string;
  service?: MachineAccountService;
  personalDatasetId?: string | null;
  personalDatasetProjectId?: string | null;
  realtimeViewId?: string | null;
  /** Organisation keys (#554): who made it. Keys belong to the organisation, not the person. */
  createdByName?: string | null;
  companyId?: string | null;
}

export interface ListApiKeysResponse {
  accounts: ApiKeyInfo[];
}

export interface AccessTokenResponse {
  userId: string;
  keyLabel: string;
  accessToken: string;
  expireTime: string;
}

// --- NEW: Interface for Delete Response ---
export interface DeleteApiKeyResponse {
  msg: string;
  uid: string;
  keyLabel: string;
}

// --- AUTH HELPER (Unchanged) ---

const getAuthHeaders = async (): Promise<HeadersInit> => {
  const auth = getAuth();
  const user = auth.currentUser;

  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (user) {
    try {
      const idToken = await user.getIdToken();
      headers["Authorization"] = `Bearer ${idToken}`;
    } catch (error) {
      console.error("Failed to get Firebase ID token:", error);
      throw new Error("Authentication required - please log in");
    }
  } else {
    throw new Error("User not authenticated - please log in");
  }

  return headers;
};

// --- API SERVICE ---

export const machineAccountsApi = {
  /**
   * Creates a new machine account or rotates the key for an existing one.
   */
  async createOrRotateApiKey(
    request: CreateMachineAccountRequest
  ): Promise<MachineAccount> {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated - please log in");
    }

    console.log("🔑 Creating/rotating machine account:", request.keyLabel);

    const url = `${BASE_URL}/users/${user.uid}/machine-accounts`;

    try {
      const headers = await getAuthHeaders();

      const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(request),
        mode: "cors",
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `API Error ${response.status}: ${errorText || response.statusText}`
        );
      }

      const result = await response.json();
      console.log("✅ Machine account created/rotated successfully");
      return result;
    } catch (error) {
      console.error("Failed to create/rotate machine account:", error);
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error(
          `Network error - check CORS and API availability: ${error.message}`
        );
      }
      throw error;
    }
  },

  /**
   * Lists all API keys (keyLabel and updatedAt) for the current user.
   */
  async listApiKeys(): Promise<ListApiKeysResponse> {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated - please log in");
    }

    console.log("Listing machine accounts...");

    const url = `${BASE_URL}/users/${user.uid}/machine-accounts/list`;

    try {
      const headers = await getAuthHeaders();

      const response = await fetch(url, {
        method: "GET",
        headers,
        mode: "cors",
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `API Error ${response.status}: ${errorText || response.statusText}`
        );
      }

      const result = await response.json();
      console.log("✅ Fetched machine accounts successfully");
      return result;
    } catch (error) {
      console.error("Failed to list machine accounts:", error);
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error(
          `Network error - check CORS and API availability: ${error.message}`
        );
      }
      throw error;
    }
  },

  /**
   * Exchanges a long-lived machine account API key for a short-lived access token.
   */
  async exchangeApiKey(apiKey: string): Promise<AccessTokenResponse> {
    // ... (code unchanged)
    console.log("Exchanging API key for access token...");
    const url = `${BASE_URL}/login-with-apikey`;
    try {
      const headers: HeadersInit = {
        "Content-Type": "application/json",
        "X-Api-Key": apiKey,
      };
      const response = await fetch(url, {
        method: "POST",
        headers,
        mode: "cors",
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `API Error ${response.status}: ${errorText || response.statusText}`
        );
      }
      const result = await response.json();
      console.log("✅ Exchanged API key successfully");
      return result;
    } catch (error) {
      console.error("Failed to exchange API key:", error);
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error(
          `Network error - check CORS and API availability: ${error.message}`
        );
      }
      throw error;
    }
  },

  /**
   * --- NEW: Deletes a machine account and revokes its key ---
   */
  async deleteApiKey(request: {
    keyLabel: string;
  }): Promise<DeleteApiKeyResponse> {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated - please log in");
    }

    console.log(`🗑️ Deleting machine account: ${request.keyLabel}`);

    const url = `${BASE_URL}/users/${user.uid}/machine-accounts/delete`;

    try {
      const headers = await getAuthHeaders();

      const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(request),
        mode: "cors",
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `API Error ${response.status}: ${errorText || response.statusText}`
        );
      }

      const result = await response.json();
      console.log("✅ Machine account deleted successfully");
      return result;
    } catch (error) {
      console.error("Failed to delete machine account:", error);
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error(
          `Network error - check CORS and API availability: ${error.message}`
        );
      }
      throw error;
    }
  },
};
